// src/api/client.ts
import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

// Типы для API ответов
export interface ApiResponse<T = any> {
  data: T
  message?: string
  status: number
}

export interface ApiError {
  detail: string
  status_code: number
}

class ApiClient {
  private client: AxiosInstance

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL || '/api/v1') {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      // Добавляем поддержку CORS
      withCredentials: false
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor для добавления токена
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor для обработки ошибок
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        console.error('API Error:', error)

        if (error.response?.status === 401) {
          // Redirect to login on unauthorized
          localStorage.removeItem('auth_token')
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }

        // Улучшаем обработку ошибок
        const apiError: ApiError = {
          detail: error.response?.data?.detail || error.message || 'Network error',
          status_code: error.response?.status || 0
        }

        return Promise.reject(apiError)
      }
    )
  }

  // GET запрос
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.get<T>(url, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // POST запрос
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.post<T>(url, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // PUT запрос
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.put<T>(url, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // DELETE запрос
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.delete<T>(url, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // PATCH запрос
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.patch<T>(url, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Обработка ошибок
  private handleError(error: any): ApiError {
    // Если это уже наша ApiError из interceptor
    if (error.detail && error.status_code !== undefined) {
      return error
    }

    if (error.response) {
      // Сервер вернул ошибку
      return {
        detail: error.response.data?.detail || error.response.statusText || 'Server error',
        status_code: error.response.status
      }
    } else if (error.request) {
      // Запрос был отправлен, но ответа нет
      return {
        detail: 'Network error: Unable to connect to server',
        status_code: 0
      }
    } else {
      // Другие ошибки
      return {
        detail: error.message || 'An unexpected error occurred',
        status_code: 500
      }
    }
  }

  // Метод для установки токена авторизации
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token)
  }

  // Метод для удаления токена авторизации
  clearAuthToken(): void {
    localStorage.removeItem('auth_token')
  }

  // Метод для загрузки файлов
  async uploadFile<T = any>(url: string, file: File, progressCallback?: (progress: number) => void): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await this.client.post<T>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressCallback && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            progressCallback(progress)
          }
        }
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }
}

// Создаем экземпляр API клиента
const apiClient = new ApiClient()

export default apiClient
